import { CATALOGS, LOCALES, LOCALE_DIRECTION, LOCALE_LABEL, type Locale, type Messages } from './catalog.js';

export { CATALOGS, LOCALES, LOCALE_DIRECTION, LOCALE_LABEL };
export type { Locale, Messages };

export type TranslateValues = Record<string, string | number | null | undefined>;

const PLACEHOLDER = /\{(\w+)\}/g;

export function translate(locale: Locale, key: string, values?: TranslateValues): string {
  const catalog = CATALOGS[locale] ?? CATALOGS.en;
  const template = catalog[key] ?? CATALOGS.en[key] ?? key;
  if (!values) return template;
  return template.replace(PLACEHOLDER, (match, name: string) => {
    const value = values[name];
    return value === undefined || value === null ? '' : String(value);
  });
}

export interface Translator {
  locale: Locale;
  dir: 'rtl' | 'ltr';
  t: (key: string, values?: TranslateValues) => string;
  has: (key: string) => boolean;
}

export function createTranslator(locale: Locale): Translator {
  const resolved: Locale = LOCALES.includes(locale) ? locale : 'en';
  return {
    locale: resolved,
    dir: LOCALE_DIRECTION[resolved],
    t: (key, values) => translate(resolved, key, values),
    has: (key) => Boolean(CATALOGS[resolved]?.[key] ?? CATALOGS.en[key]),
  };
}

export function normalizeLocale(input: string | null | undefined, fallback: Locale = 'fa'): Locale {
  if (!input) return fallback;
  const lower = input.toLowerCase();
  if (lower.startsWith('fa') || lower.startsWith('pe')) return 'fa';
  if (lower.startsWith('en')) return 'en';
  return fallback;
}

/**
 * Wrap a Jira key or other technical token so it stays left-to-right inside
 * right-to-left text. Without this, `ABC-123` renders as `123-ABC` in Persian.
 */
export function ltr(text: string): string {
  return `⁦${text}⁩`;
}

export function missingKeys(): Record<Locale, string[]> {
  const all = new Set<string>();
  for (const catalog of Object.values(CATALOGS)) {
    for (const key of Object.keys(catalog)) all.add(key);
  }
  const out = {} as Record<Locale, string[]>;
  for (const locale of LOCALES) {
    out[locale] = [...all].filter((key) => !(CATALOGS[locale] as Messages)[key]).sort();
  }
  return out;
}
